import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { BillingToggle, type BillingTerm } from "~/components/billing-toggle";
import { Arrow, ProductGlyph } from "~/components/icons";
import { PlanCards } from "~/components/product";
import { PlanComparison } from "~/components/plan-comparison";
import { AvailabilityBadge } from "~/components/regions";
import { Shell } from "~/components/site-shell";
import { getProduct } from "~/config/products";
import { breadcrumbs, jsonLd, productJsonLd, seoHead } from "~/config/seo";

export const Route = createFileRoute("/products/web-hosting/shared-hosting")({
  head: () => {
    const product = getProduct("web-hosting", "shared-hosting");
    return {
      ...seoHead({
        title:
          "App and Website Hosting for Node.js, Python, PHP and WordPress | HostRight",
        description:
          "Host websites, Node.js apps, Python projects, and WordPress without managing a server. Plans include SSL, databases, email, backups, and support.",
        path: "/products/web-hosting/shared-hosting",
        type: "product",
      }),
      scripts: product
        ? [
            jsonLd(
              productJsonLd(product, "/products/web-hosting/shared-hosting"),
            ),
            jsonLd(
              breadcrumbs([
                { name: "Home", path: "/" },
                { name: "Web hosting", path: "/products/web-hosting" },
                {
                  name: "Shared hosting",
                  path: "/products/web-hosting/shared-hosting",
                },
              ]),
            ),
          ]
        : [],
    };
  },
  component: SharedHosting,
});

const hostingWorkloads = [
  {
    title: "PHP applications",
    description:
      "Host PHP applications either built with a framework like Laravel or WordPress, or custom-built from scratch.",
    icon: "domain",
  },
  {
    title: "Node.js applications",
    description:
      "Run Express, Next.js backends, APIs, dashboards, bots, and other Node.js projects.",
    icon: "app",
  },
  {
    title: "Python WSGI applications",
    description:
      "Deploy Django, Flask, and other WSGI compatible Python web applications with a managed runtime.",
    icon: "container",
  },
  {
    title: "One-click applications",
    description:
      "Install useful open-source applications without configuring databases, web servers, and SSL by hand.",
    icon: "database",
  },
] as const;

const comparisonLabels = new Set([
  "Websites / apps",
  "Databases",
  "Email accounts",
  "Storage",
  "CPU",
  "RAM",
  "Backup cadence",
  "SSL certificate",
  "DNS management",
  "One-click installer",
]);

const faqs = [
  {
    question: "Can I host a Node.js application on HostRight?",
    answer:
      "Yes. Node.js application setup is included across the shared hosting plans. The plan you choose changes the number of websites and apps, databases, email addresses, and storage available.",
  },
  {
    question: "Can I deploy Django and Python applications?",
    answer:
      "Yes. Python application setup is included, so you can deploy Django, Flask, and other WSGI compatible Python web applications.",
  },
  {
    question: "Is WordPress hosting included?",
    answer:
      "Yes. WordPress is a first-class workload. Use the one-click installer, connect your domain, and manage WordPress alongside custom websites and applications.",
  },
  {
    question: "Do the plans include email hosting?",
    answer:
      "Yes. Every plan includes email accounts, with the number of available accounts shown in the pricing cards and comparison table.",
  },
  {
    question: "Do I need to manage a VPS or Linux server?",
    answer:
      "No. HostRight manages the core hosting environment while giving you practical tools such as application setup, Git, SSH, cron jobs, and backups.",
  },
  {
    question: "Can HostRight help migrate an existing website?",
    answer:
      "Migration guidance is included. Contact the team if your project needs help planning the move.",
  },
] as const;

function SharedHosting() {
  const product = getProduct("web-hosting", "shared-hosting");
  const [billingTerm, setBillingTerm] = useState<BillingTerm>("monthly");

  return (
    <Shell>
      <section className="border-b border-ink/10 bg-cream">
        <div className="mx-auto max-w-7xl px-5 py-12 lg:px-12 lg:py-16">
          <a
            href="/products/web-hosting"
            className="eyebrow text-moss hover:underline"
          >
            Web hosting
          </a>
          <div className="mt-4 grid gap-6 lg:grid-cols-[1fr_.8fr] lg:items-end">
            <h1 className="display max-w-3xl text-5xl font-bold leading-[.93] lg:text-6xl">
              Hosting plans for websites, apps, and WordPress.
            </h1>
            <div>
              <p className="max-w-xl text-lg leading-8 text-ink/65">
                Managed hosting for websites and applications, with SSL,
                databases, email, backups, and a familiar control panel
                included.
              </p>
              <AvailabilityBadge />
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-ink/10 bg-white">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-12">
          <p className="eyebrow text-moss">More than traditional web hosting</p>
          <h2 className="display mt-3 max-w-3xl text-5xl font-bold leading-[.93]">
            One hosting platform for websites, apps, and APIs.
          </h2>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {hostingWorkloads.map((workload) => (
              <article
                key={workload.title}
                className="rounded-2xl border border-ink/10 bg-cream p-6"
              >
                <ProductGlyph type={workload.icon} />
                <h3 className="mt-6 text-lg font-bold">{workload.title}</h3>
                <p className="mt-3 text-sm leading-6 text-ink/60">
                  {workload.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section id="plans" className="scroll-mt-24 bg-white">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-12">
          <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
            <div>
              <p className="eyebrow text-moss">Pricing</p>
              <h2 className="display mt-3 max-w-2xl text-5xl font-bold leading-[.93]">
                Choose the capacity your project needs.
              </h2>
              <p className="mt-5 max-w-xl text-lg leading-8 text-ink/60">
                Start with one project, then move up as your websites, apps, or
                team need more room.
              </p>
            </div>
            <div className="flex flex-col items-start gap-3 lg:items-end">
              <p className="text-sm font-semibold text-moss lg:text-right">
                Save more with longer terms
              </p>
              <BillingToggle value={billingTerm} onChange={setBillingTerm} />
            </div>
          </div>
          <div className="mt-10">
            <PlanCards billingTerm={billingTerm} includeCustom />
          </div>
          <div
            id="included"
            className="scroll-mt-24 mt-8 rounded-2xl border border-ink/10 bg-cream px-6 py-5"
          >
            <p className="font-bold">Included with every plan</p>
            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm font-semibold text-ink/65">
              {[
                "SSL certificates",
                "PHP/Python/Node.js runtimes",
                "MariaDB Database",
                "Redis",
                "SMTP/IMAP Email",
                "DNS management",
                "Backups",
                "Technical support",
              ].map((item) => (
                <span key={item} className="inline-flex items-center gap-2">
                  <span className="size-1.5 rounded-full bg-leaf" />
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {product?.technicalRows && (
        <PlanComparison
          productName={product.name}
          plans={[
            { name: "Starter", price: "$2.99" },
            { name: "Growth", price: "$8.99" },
            { name: "Business", price: "$15.99" },
          ]}
          rows={product.technicalRows.filter((row) =>
            comparisonLabels.has(row.label),
          )}
          guide={product.comparisonGuide}
          billingTerm={billingTerm}
        />
      )}

      <section className="border-b border-ink/10 bg-white">
        <div className="mx-auto max-w-5xl px-5 py-16 lg:px-8">
          <div>
            <p className="eyebrow text-moss">Questions, answered</p>
            <h2 className="display mt-3 max-w-2xl text-5xl font-bold leading-[.93]">
              Before you choose a plan.
            </h2>
          </div>
          <div className="mt-10 divide-y divide-ink/10 border-y border-ink/10">
            {faqs.map((faq) => (
              <details key={faq.question} className="group py-5">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-5 font-bold marker:hidden">
                  {faq.question}
                  <span className="text-xl font-normal text-moss transition group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="mt-3 max-w-3xl pr-10 leading-7 text-ink/60">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink">
        <div className="mx-auto max-w-5xl px-5 py-16 text-center text-cream lg:px-8">
          <p className="eyebrow text-leaf">Ready to build?</p>
          <h2 className="display mx-auto mt-4 max-w-3xl text-5xl font-bold leading-[.93] lg:text-6xl">
            Host the project, not the server.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-lg leading-8 text-cream/70">
            Deploy websites and applications with the infrastructure already
            handled.
          </p>
          <a
            href="#plans"
            className="button-accent mt-8 inline-flex rounded-full px-8 py-4 text-base font-bold shadow-sm transition motion-safe:hover:-translate-y-0.5 focus-visible:outline-3 focus-visible:outline-leaf focus-visible:outline-offset-3"
          >
            Choose your plan <Arrow />
          </a>
        </div>
      </section>
    </Shell>
  );
}
